import {
  useCachedBusinessAddress,
  useCachedBusinessInsights,
  useCachedBusinessName,
  useCachedConversationalWritingRule,
  useCachedGeographicFocus,
  useCachedLLmsRule,
  useCachedRobotsTxtRule,
  useCachedSchemaRule,
  useCachedSelectedProject,
  useCachedStatisticalEvidenceRule,
  useCachedTargetAudience,
  useCachedVisibilityScore,
} from 'hooks';
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useCachedContentFreshnessRule } from 'hooks/api/cached/useCachedContentFreshnessRule';
import { userApiSlice } from 'store/api/userApiSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';

const STAGE_KEYS = [
  'stageTechnicalAccessibility',
  'stageBusinessInsights',
  'stageVisibilityScore',
  'stageInfoCompleteness',
  'stageVisibilityImprovements',
] as const;

type StageKey = (typeof STAGE_KEYS)[number];

type ProgressData = object;

export const RUN_BLOCK_KEYS = [
  'robotsTxt',
  'llmsTxt',
  'schemaOrg',
  'businessInsights',
  'visibilityScore',
  'businessName',
  'businessAddress',
  'targetAudience',
  'geographicFocus',
  'conversationalWriting',
  'statisticalEvidence',
  'contentFreshness',
] as const;

export type RunBlockKey = (typeof RUN_BLOCK_KEYS)[number];

export type RunBlockStatus = 'queued' | 'in-progress' | 'ready';

export interface ReportProgressStage {
  key: StageKey;
  substeps: boolean[];
  completed: boolean;
}

export interface ReportProgressState {
  stages: ReportProgressStage[];
  currentStageIndex: number;
  completedCount: number;
  totalStages: number;
  allComplete: boolean;
  isVisible: boolean;
  blocks: Record<RunBlockKey, RunBlockStatus>;
  blockStartedAt: Record<RunBlockKey, string | null>;
  isRunActive: boolean;
}

const EMPTY_BLOCK_STARTED_AT: Record<RunBlockKey, string | null> =
  RUN_BLOCK_KEYS.reduce(
    (acc, key) => {
      acc[key] = null;
      return acc;
    },
    {} as Record<RunBlockKey, string | null>
  );

let blockStartedAtByDomain: Record<
  string,
  Record<RunBlockKey, string | null>
> = {};
let wasRunActiveByDomain: Record<string, boolean> = {};
const blockStartedAtListeners = new Set<() => void>();

const subscribeToBlockStartedAt = (listener: () => void) => {
  blockStartedAtListeners.add(listener);
  return () => blockStartedAtListeners.delete(listener);
};

const getBlockStartedAtSnapshot = (domainKey: string) =>
  blockStartedAtByDomain[domainKey] ?? EMPTY_BLOCK_STARTED_AT;

const syncBlockStartedAt = (
  domainKey: string,
  isActive: boolean,
  blocks: Record<RunBlockKey, RunBlockStatus>
) => {
  let changed = false;
  let domainStore = blockStartedAtByDomain[domainKey] ?? EMPTY_BLOCK_STARTED_AT;

  if (isActive && !wasRunActiveByDomain[domainKey]) {
    domainStore = { ...EMPTY_BLOCK_STARTED_AT };
    changed = true;
  }
  if (wasRunActiveByDomain[domainKey] !== isActive) {
    wasRunActiveByDomain = { ...wasRunActiveByDomain, [domainKey]: isActive };
  }

  RUN_BLOCK_KEYS.forEach(key => {
    if (blocks[key] === 'in-progress' && !domainStore[key]) {
      domainStore = { ...domainStore, [key]: new Date().toISOString() };
      changed = true;
    }
  });

  if (changed) {
    blockStartedAtByDomain = {
      ...blockStartedAtByDomain,
      [domainKey]: domainStore,
    };
    blockStartedAtListeners.forEach(listener => listener());
  }
};

const buildBlocks = (
  substepsInOrder: boolean[],
  isRunActive: boolean,
  isPendingBackendStart: boolean
): Record<RunBlockKey, RunBlockStatus> => {
  const firstPendingIndex = substepsInOrder.findIndex(done => !done);

  return RUN_BLOCK_KEYS.reduce(
    (acc, key, index) => {
      if (!isRunActive) {
        acc[key] = 'ready';
        return acc;
      }

      if (isPendingBackendStart) {
        acc[key] = 'queued';
        return acc;
      }

      if (substepsInOrder[index]) {
        acc[key] = 'ready';
        return acc;
      }

      acc[key] = index === firstPendingIndex ? 'in-progress' : 'queued';
      return acc;
    },
    {} as Record<RunBlockKey, RunBlockStatus>
  );
};

const STAGE_SUBSTEP_COUNTS = [3, 1, 1, 4, 3] as const;
const TOTAL_STAGES = STAGE_KEYS.length;
const EMPTY_STAGES: ReportProgressStage[] = STAGE_KEYS.map((key, index) => ({
  key,
  substeps: Array.from({ length: STAGE_SUBSTEP_COUNTS[index] }, () => false),
  completed: false,
}));

const hasResponseField = (
  data: ProgressData | null | undefined,
  field: string
) =>
  (data as { statusCode?: number } | null | undefined)?.statusCode !== 202 &&
  data !== null &&
  data !== undefined &&
  field in (data as Record<string, unknown>);

const createStage = (
  key: StageKey,
  substeps: boolean[]
): ReportProgressStage => ({
  key,
  substeps,
  completed: substeps.every(Boolean),
});

export const useReportProgress = (): ReportProgressState => {
  const { reportGenerating, selectedProject } = useCachedSelectedProject();
  const domainKey = selectedProject?.domain ?? '';
  const dispatch = useAppDispatch();
  const historyMode = useAppSelector(state => state.history.historyMode);
  const isGenerating = useAppSelector(
    state => state.reportGeneration.isGenerating
  );
  const didInvalidateUserInfoOnCompleteRef = useRef(false);

  const { insights } = useCachedBusinessInsights();
  const { businessName } = useCachedBusinessName();
  const { businessAddress } = useCachedBusinessAddress();
  const { targetAudience } = useCachedTargetAudience();
  const { geographicFocus } = useCachedGeographicFocus();
  const { visibilityScore } = useCachedVisibilityScore();
  const { ruleData: robotsTxt } = useCachedRobotsTxtRule();
  const { ruleData: llmsTxt } = useCachedLLmsRule();
  const { ruleData: schemaOrg } = useCachedSchemaRule();
  const { ruleData: conversational } = useCachedConversationalWritingRule();
  const { ruleData: statistical } = useCachedStatisticalEvidenceRule();
  const { ruleData: freshness } = useCachedContentFreshnessRule();

  const isActive = !historyMode && (reportGenerating || isGenerating);
  const isPendingBackendStart = isGenerating && !reportGenerating;

  const blockStartedAt = useSyncExternalStore(subscribeToBlockStartedAt, () =>
    getBlockStartedAtSnapshot(domainKey)
  );

  const stages = isPendingBackendStart
    ? EMPTY_STAGES
    : [
        createStage(STAGE_KEYS[0], [
          hasResponseField(robotsTxt, 'score'),
          hasResponseField(llmsTxt, 'score'),
          hasResponseField(schemaOrg, 'score'),
        ]),
        createStage(STAGE_KEYS[1], [
          hasResponseField(insights, 'business_profile'),
        ]),
        createStage(STAGE_KEYS[2], [
          hasResponseField(visibilityScore, 'products'),
        ]),
        createStage(STAGE_KEYS[3], [
          hasResponseField(businessName, 'business_name_identified'),
          hasResponseField(businessAddress, 'business_address_extraction'),
          hasResponseField(targetAudience, 'company_analysis'),
          hasResponseField(geographicFocus, 'company_analysis'),
        ]),
        createStage(STAGE_KEYS[4], [
          hasResponseField(conversational, 'products'),
          hasResponseField(statistical, 'products'),
          hasResponseField(freshness, 'products'),
        ]),
      ];

  const completedCount = stages.filter(s => s.completed).length;
  const currentStageIndex = stages.findIndex(s => !s.completed);
  const allComplete = completedCount === TOTAL_STAGES;
  const resolvedCurrentStageIndex =
    currentStageIndex === -1 ? TOTAL_STAGES - 1 : currentStageIndex;

  const blocks = buildBlocks(
    stages.flatMap(stage => stage.substeps),
    isActive,
    isPendingBackendStart
  );

  useEffect(() => {
    if (allComplete && reportGenerating) {
      if (!didInvalidateUserInfoOnCompleteRef.current) {
        didInvalidateUserInfoOnCompleteRef.current = true;
        dispatch(userApiSlice.util.invalidateTags(['UserInfo']));
      }
      return;
    }

    didInvalidateUserInfoOnCompleteRef.current = false;
  }, [allComplete, dispatch, reportGenerating]);

  useEffect(() => {
    syncBlockStartedAt(domainKey, isActive, blocks);
  });

  return {
    stages,
    currentStageIndex: resolvedCurrentStageIndex,
    completedCount,
    totalStages: TOTAL_STAGES,
    allComplete,
    isVisible: isActive,
    blocks,
    blockStartedAt,
    isRunActive: isActive,
  };
};
